/**
 * AppSandbox — unified lifecycle facade implementation.
 *
 * Orchestrates the create/mount/unmount/destroy lifecycle for all 6 sandbox modules.
 * This is the ONLY surface the Page Editor interacts with; it is transparent to app code.
 *
 * Design: .planning/phases/151-sandbox-architecture-design/APPSANDBOX-UNIFIED-API.md
 * Interface: ./app-sandbox.types.ts
 * Phase 152 Plan 04.
 */

import { createNavigationSandbox } from './navigation-sandbox.js'
import { createStorageSandbox } from './storage-sandbox.js'
import { createEventSandbox } from './event-sandbox.js'
import { createDomSandbox } from './dom-sandbox.js'
import { createTimerSandbox } from './timer-sandbox.js'
import { createNetworkSandbox } from './network-sandbox.js'
import type {
  AppSandbox,
  AppSandboxOptions,
  AppSandboxState,
  SandboxModules,
  CreateAppSandbox,
} from './app-sandbox.types.js'

/**
 * Creates a new AppSandbox instance that orchestrates all 6 sandbox modules.
 *
 * Lifecycle states:
 *   'created'   → 'mounted'   (via mount())
 *   'mounted'   → 'unmounted' (via unmount())
 *   'unmounted' → 'mounted'   (via mount() again — tab switching)
 *   any state   → 'destroyed' (via destroy())
 *   'destroyed' → terminal — all methods become no-ops
 *
 * Destroy order is critical (must match APPSANDBOX-UNIFIED-API.md):
 *   1. EventSandbox   — stop event forwarding first
 *   2. TimerSandbox   — cancel pending timers
 *   3. NavigationSandbox — clear navigation stack
 *   4. StorageSandbox — release references
 *   5. NetworkSandbox — no-op, lifecycle consistency
 *   6. DomSandbox     — remove portal and styles LAST
 */
export const createAppSandbox: CreateAppSandbox = (options: AppSandboxOptions): AppSandbox => {
  // ─── Create all 6 modules ──────────────────────────────────────────────
  const nav = createNavigationSandbox({
    initialPathname: options.initialPathname,
    onNavigationAttempt: options.onNavigationAttempt,
  })
  const storage = createStorageSandbox({ appId: options.appId })
  const events = createEventSandbox({ appId: options.appId })
  const dom = createDomSandbox({ appId: options.appId })
  const timer = createTimerSandbox()
  const network = createNetworkSandbox({ apiBaseUrl: options.apiBaseUrl })

  const modules: SandboxModules = { navigation: nav, storage, events, dom, timer, network }
  let currentState: AppSandboxState = 'created'
  let currentDesignTokens = options.designTokens

  const sandbox: AppSandbox = {
    get modules(): SandboxModules {
      return modules
    },

    get state(): AppSandboxState {
      return currentState
    },

    /**
     * Mount the sandbox to the app container element.
     * - Wires EventSandbox capture-phase forwarder to containerEl
     * - Creates DomSandbox portal container inside containerEl with design tokens
     * - Transitions: 'created' | 'unmounted' → 'mounted'
     * - No-op if already 'destroyed'
     */
    mount(containerEl: HTMLElement): void {
      if (currentState === 'destroyed') return
      events.mount(containerEl)
      dom.mount(currentDesignTokens, containerEl)
      currentState = 'mounted'
    },

    /**
     * Unmount the sandbox (pause — not destroy).
     * - Removes EventSandbox capture listener
     * - Removes DomSandbox portal from DOM (dom.unmount() NOT dom.destroy())
     *   → portal removed, DomSandbox instance stays alive (destroyed=false)
     *   → allows re-mount via mount() which calls dom.mount() again
     * - Transitions: 'mounted' → 'unmounted'
     * - No-op if not 'mounted'
     */
    unmount(): void {
      if (currentState !== 'mounted') return
      events.unmount()
      // NOTE: dom.unmount() removes portal from DOM but keeps DomSandbox alive.
      // This allows re-mount for tab switching (dom.mount() can be called again).
      // dom.destroy() would set destroyed=true permanently — do NOT use here.
      dom.unmount()
      currentState = 'unmounted'
    },

    /**
     * Update app design tokens (called on theme change in Page Editor).
     * In the created state, cache the newest tokens so the first mount does not
     * render a stale design style if Studio changes tokens while the lazy
     * renderer/sandbox is still loading. Mounted/unmounted sandboxes also push
     * the update into the app-owned DOM roots.
     */
    updateTokens(designTokens: Record<string, string>): void {
      if (currentState === 'destroyed') return
      currentDesignTokens = designTokens
      if (currentState === 'created') return
      dom.updateTokens(designTokens)
    },

    /**
     * Destroy the sandbox permanently.
     * Destroys all modules in spec order — this order is critical for correctness.
     * Transitions to 'destroyed' state — all subsequent method calls become no-ops.
     */
    destroy(): void {
      if (currentState === 'destroyed') return
      // Destroy order: EventSandbox(1) → TimerSandbox(2) → NavigationSandbox(3)
      //                → StorageSandbox(4) → NetworkSandbox(5) → DomSandbox(6)
      events.destroy()      // 1. Remove event capture first (prevents stale forwarding)
      timer.destroy()       // 2. Clear all pending setTimeout/setInterval/rAF
      nav.destroy()         // 3. Clear sandbox navigation stack
      storage.destroy()     // 4. Release storage wrapper refs (app data persists in localStorage)
      network.destroy()     // 5. No-op — stateless, but maintains lifecycle consistency
      dom.destroy()         // 6. Remove portal container and injected styles LAST
      currentState = 'destroyed'
    },
  }

  return sandbox
}
